import { useState, useEffect } from "react";
import { useTicketNotificationStore } from "@/stores/ticketNotificationStore";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, MessageCircle, Settings, ChevronDown, Bell, Film, Plus, Eye, X, BarChart3, Code, Users, Activity, HelpCircle, BookOpen, Lightbulb, FolderOpen, Sparkles, Tags, ClipboardList, Clock, Crown, ListChecks, ImageIcon, Bug, Rocket, Shield } from "lucide-react";
import {
  IconCalendar,
} from "@tabler/icons-react";
import prsnlLogo from "@/assets/prsnl-logo.svg";
import {
  Sidebar,
  SidebarBody,
  SidebarLink,
} from "@/components/ui/aceternity-sidebar";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { useUserStore } from "@/stores/userStore";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useServiceAccess } from "@/hooks/useServiceAccess";
import { useImpersonationStore, getImpersonatedDisplayName } from "@/stores/impersonationStore";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BugReportModal } from "@/components/BugReportModal";
import { FeatureRequestModal } from "@/components/FeatureRequestModal";
import { useBugReportStore } from "@/stores/bugReportStore";

// Helper functions for consistent avatar styling across the app
function getInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
}

function getGradientFromName(firstName?: string | null, lastName?: string | null): string {
  const name = `${firstName || ''}${lastName || ''}`;
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
  ];
  
  return gradients[Math.abs(hash) % gradients.length];
}

interface DesktopSidebarProps {
  readonly currentView: string;
  readonly onChangeView: (view: string) => void;
  readonly onOpenMessaging: () => void;
  readonly onLogout: () => void;
  readonly unreadMessages: number;
  readonly openTicketCount?: number;
  readonly hasKnowledgeBaseWarning?: boolean;
  readonly hasProfileIncomplete?: boolean;
  readonly onOpenScheduling?: () => void;
  readonly onExpandedChange?: (expanded: boolean) => void;
  readonly hideBookingTab?: boolean;
}

// Map view names to service names in role_permissions
const VIEW_TO_SERVICE: Record<string, string> = {
  'admin-create-user': 'admin_create_user',
  'admin-delete-user': 'admin_delete_user',
  'admin-notifications': 'admin_notifications',
  'admin-feature-access': 'admin_feature_access',
  'admin-fake-content': 'admin_fake_content',
  'admin-user-view': 'admin_user_view',
  'admin-users': 'admin_feature_access',
  'admin-stats': 'admin_stats',
  'admin-api-test': 'admin_stats',
  'admin-sync-dashboard': 'admin_stats',
  'admin-documentation': 'admin_stats',
  'user-stats': 'user_stats',
  'pbm-topics': 'pbm_topics',
  'user-topics': 'user_topics',
  'assets': 'rushes_manager',
  'staff-chat': 'staff_chat',
  'admin-task-builder': 'admin_task_builder',
  'admin-studios': 'admin_feature_access',
  'ops-tickets': 'admin_task_builder',
  'csm-dashboard': 'csm_dashboard',
  'csm-tasks': 'csm_dashboard',
  'tech-bug-tracker': 'tech_bug_tracker',
  'product-board': 'admin_feature_access',
  'qa-runs': 'admin_feature_access',
  'marker-type-config': 'admin_marker_config',
};

const adminMenuItems = [
  { label: "Gestion utilisateurs", view: "admin-users", icon: Users },
  { label: "Vue Utilisateur", view: "admin-user-view", icon: Eye },
  { label: "Notifications", view: "admin-notifications", icon: Bell },
  { label: "Task Builder AI", view: "admin-task-builder", icon: Sparkles },
  { label: "Product Board", view: "product-board", icon: Lightbulb },
  { label: "QA Runs", view: "qa-runs", icon: Shield },
  { label: "Fake content", view: "admin-fake-content", icon: Film },
  { label: "Échéances post-prod", view: "admin-postproduction-deadlines", icon: Clock },
  { label: "Studios", view: "admin-studios", icon: Building2 },
  
  { label: "Statistiques", view: "admin-stats", icon: BarChart3 },
  { label: "Onboarding", view: "admin-onboarding", icon: Lightbulb },
  { label: "Sync Queue", view: "admin-sync-dashboard", icon: Activity },
  { label: "API Test", view: "admin-api-test", icon: Code },
  { label: "Documentation", view: "admin-documentation", icon: BookOpen },
  { label: "Config marqueurs", view: "marker-type-config", icon: Tags },
];

interface AdminMenuProps {
  readonly adminMenuOpen: boolean;
  readonly setAdminMenuOpen: (open: boolean) => void;
  readonly onChangeView: (view: string) => void;
  readonly currentView: string;
  readonly hasAccess: (service: string) => boolean;
  readonly sidebarOpen: boolean;
  readonly animate: boolean;
}

// Helper function to compute animation display property
function getAnimationDisplay(animate: boolean, open: boolean): "inline-block" | "none" {
  if (!animate) return "inline-block";
  return open ? "inline-block" : "none";
}

// Helper function to compute animation opacity
function getAnimationOpacity(animate: boolean, open: boolean): number {
  if (!animate) return 1;
  return open ? 1 : 0;
}

function AdminMenu({ adminMenuOpen, setAdminMenuOpen, onChangeView, currentView, hasAccess, sidebarOpen, animate }: AdminMenuProps) {
  const open = sidebarOpen;

  const isAdminViewActive = currentView.startsWith('admin-');

  // Find the current admin menu item to show its icon when sidebar is closed
  const currentAdminItem = adminMenuItems.find(item => item.view === currentView);
  const CurrentIcon = currentAdminItem?.icon || Settings;

  // Pre-compute animation values to avoid nested ternaries
  const displayValue = getAnimationDisplay(animate, open);
  const opacityValue = getAnimationOpacity(animate, open);

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setAdminMenuOpen(!adminMenuOpen)}
        className={cn(
          "flex items-center group/sidebar rounded-lg transition-all duration-200 w-full",
          open ? "justify-start gap-2 px-3 py-2" : "justify-center w-9 h-9",
          isAdminViewActive
            ? "bg-neutral-300 dark:bg-neutral-700/60"
            : "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-300/50 dark:hover:bg-neutral-700/30"
        )}
      >
        {/* Show current submenu icon when closed, Settings icon when open */}
        {open ? (
          <Settings className="h-5 w-5 flex-shrink-0 transition-colors text-neutral-700 dark:text-neutral-200" />
        ) : (
          <CurrentIcon className={cn("h-5 w-5 flex-shrink-0 transition-colors", isAdminViewActive ? "text-warning" : "text-neutral-700 dark:text-neutral-200")} />
        )}
        <motion.span
          animate={{
            display: displayValue,
            opacity: opacityValue,
          }}
          className="text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0 text-neutral-700 dark:text-neutral-200"
        >
          Administration
        </motion.span>
        <motion.div
          animate={{
            display: displayValue,
            opacity: opacityValue,
          }}
          className="ml-auto"
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              adminMenuOpen && "rotate-180"
            )}
          />
        </motion.div>
      </button>

      <AnimatePresence>
        {adminMenuOpen && open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pl-4 mt-1 flex flex-col gap-1">
              {adminMenuItems
                .filter((item) => {
                  const service = VIEW_TO_SERVICE[item.view];
                  return service ? hasAccess(service) : true;
                })
                .map((item) => {
                  const isActive = currentView === item.view;
                  return (
                    <button
                      type="button"
                      key={item.view}
                      onClick={() => onChangeView(item.view)}
                      className={cn(
                        "flex items-center justify-start gap-2 group/sidebar py-2 px-2 rounded-lg transition-colors duration-150 w-full text-left",
                        isActive
                          ? "bg-neutral-300 dark:bg-neutral-700/60"
                          : "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-300/50 dark:hover:bg-neutral-700/30"
                      )}
                    >
                      <item.icon className={cn("h-4 w-4 flex-shrink-0 transition-colors", isActive && "!text-warning")} />
                      <span className={cn(
                        "text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre",
                        isActive && "!text-warning font-medium"
                      )}>
                        {item.label}
                      </span>
                    </button>
                  );
                })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function DesktopSidebar({
  currentView,
  onChangeView,
  onOpenMessaging,
  onLogout,
  unreadMessages,
  openTicketCount = 0,
  hasKnowledgeBaseWarning,
  hasProfileIncomplete,
  onOpenScheduling,
  onExpandedChange,
  hideBookingTab = false,
}: DesktopSidebarProps) {
  const [open, setOpen] = useState(true);
  
  // Notify parent when expanded state changes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onExpandedChange?.(newOpen);
  };
  // Keep admin menu open if an admin view is active
  const isAdminViewActive = currentView.startsWith('admin-');
  const [adminMenuOpen, setAdminMenuOpen] = useState(isAdminViewActive);
  
  // Automatically open admin menu when an admin view is selected
  useEffect(() => {
    if (isAdminViewActive && !adminMenuOpen) {
      setAdminMenuOpen(true);
    }
  }, [isAdminViewActive]);

  // Close admin menu when sidebar collapses
  useEffect(() => {
    if (!open) {
      setAdminMenuOpen(false);
    }
  }, [open]);
  const { userEmail, userId } = useAuthStore();
  // Use reactive selectors for Zustand reactivity
  const fetchUserProfile = useUserStore(state => state.fetchUserProfile);
  const getUserFullName = useUserStore(state => state.getUserFullName);
  const userProfile = useUserStore(state => userId ? state.profiles[userId] : null);
  
  const { isAdmin } = useIsAdmin();
  const { hasAccess, isStaff: isUserStaff } = useServiceAccess();
  const [showBugReportModal, setShowBugReportModal] = useState(false);
  const [showFeatureRequestModal, setShowFeatureRequestModal] = useState(false);
  const { isImpersonating, impersonatedUser, stopImpersonation } = useImpersonationStore();
  const hasUnseenTickets = useTicketNotificationStore(state => state.hasUnseenActivity);
  
  const [roleDisplayName, setRoleDisplayName] = useState<string | null>(null);
  
  useEffect(() => {
    if (!userId) return;

    fetchUserProfile(userId);

    const loadUserRoles = async () => {
      const { getUserPrimaryRoleDisplayName, isStaff } = await import('@/services/data/roles');

      const staffResult = await isStaff(userId);
      if (staffResult.success && staffResult.data) {
        const result = await getUserPrimaryRoleDisplayName(userId);
        if (result.success && result.data) {
          setRoleDisplayName(result.data);
        }
      } else {
        setRoleDisplayName(null);
      }
    };

    loadUserRoles();
  }, [userId, fetchUserProfile]);
  
  const displayName = userId ? getUserFullName(userId, userEmail || undefined) : userEmail || "User";
  
  // Get impersonated user initials
  const getImpersonatedInitials = () => {
    if (!impersonatedUser) return '?';
    if (impersonatedUser.first_name && impersonatedUser.last_name) {
      return `${impersonatedUser.first_name[0]}${impersonatedUser.last_name[0]}`.toUpperCase();
    }
    if (impersonatedUser.first_name) {
      return impersonatedUser.first_name.slice(0, 2).toUpperCase();
    }
    return impersonatedUser.email.slice(0, 2).toUpperCase();
  };

  const links = [
    {
      label: "Planning",
      href: "#",
      icon: (
        <div className="relative">
          <IconCalendar className={cn(
            "h-5 w-5 flex-shrink-0",
            isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
          )} />
          {hasKnowledgeBaseWarning && (
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-warning rounded-full" />
          )}
        </div>
      ),
      onClick: () => onChangeView('calendar'),
    },
    // Conditionally insert user stats after Planning
    ...(hasAccess('user_stats') ? [{
      label: "Statistiques",
      href: "#",
      icon: (
        <BarChart3 className={cn(
          "h-5 w-5 flex-shrink-0",
          isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
        )} />
      ),
      onClick: () => onChangeView('user-stats'),
    }] : []),
    // Assets - conditionally shown based on access
    ...(hasAccess('rushes_manager') ? [{
      label: "Assets",
      href: "#",
      icon: (
        <FolderOpen className={cn(
          "h-5 w-5 flex-shrink-0",
          isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
        )} />
      ),
      onClick: () => onChangeView('assets'),
    }] : []),
    // Staff chat - for PBM, Ops, Admin to chat with customers
    ...(hasAccess('staff_chat') ? [{
      label: "Conversations",
      href: "#",
      icon: (
        <div className="relative">
          <MessageCircle className={cn(
            "h-5 w-5 flex-shrink-0",
            isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
          )} />
          {/* Badge for unread messages */}
          {unreadMessages > 0 && (
            <span className="absolute -top-1 -right-1 bg-warning text-warning-foreground text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
              {unreadMessages}
            </span>
          )}
          {/* Dot indicator for unseen ticket activity (only show if no unread badge) */}
          {unreadMessages === 0 && hasUnseenTickets && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-warning rounded-full" />
          )}
        </div>
      ),
      onClick: () => onChangeView('staff-chat'),
    }] : [{
      // Customer chat (internal chat system)
      label: "Messages",
      href: "#",
      icon: (
        <div className="relative">
          <MessageCircle className={cn(
            "h-5 w-5 flex-shrink-0",
            isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
          )} />
          {unreadMessages > 0 && (
            <span className="absolute -top-1 -right-1 bg-warning text-warning-foreground text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
              {unreadMessages}
            </span>
          )}
        </div>
      ),
      onClick: () => onChangeView('customer-chat'),
    }]),
  ];

  const bookingLink = {
    label: "Réserver un tournage",
    href: "#",
    icon: (
      <Plus className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
    ),
    onClick: () => onOpenScheduling?.(),
  };

  const helpLink = {
    label: "Aide",
    href: "#",
    icon: (
      <HelpCircle className={cn(
        "h-5 w-5 flex-shrink-0",
        isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
      )} />
    ),
    onClick: () => onChangeView('help'),
  };

const directorShootingsLink = {
    label: "Shootings",
    href: "#",
    icon: (
      <Film className={cn(
        "h-5 w-5 flex-shrink-0",
        isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
      )} />
    ),
    onClick: () => onChangeView('director-dashboard'),
  };

  const opsTicketsLink = {
    label: "Tableau tâches",
    href: "#",
    icon: (
      <ClipboardList className={cn(
        "h-5 w-5 flex-shrink-0",
        isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
      )} />
    ),
    onClick: () => onChangeView('ops-tickets'),
  };

  const csmDashboardLink = {
    label: "Suivi clients",
    href: "#",
    icon: (
      <Crown className={cn(
        "h-5 w-5 flex-shrink-0",
        isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
      )} />
    ),
    onClick: () => onChangeView('csm-dashboard'),
  };

  const csmTasksLink = {
    label: "Tâches CSM",
    href: "#",
    icon: (
      <ListChecks className={cn(
        "h-5 w-5 flex-shrink-0",
        isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
      )} />
    ),
    onClick: () => onChangeView('csm-tasks'),
  };

  const miniamakerDashboardLink = {
    label: "Miniatures",
    href: "#",
    icon: (
      <ImageIcon className={cn(
        "h-5 w-5 flex-shrink-0",
        isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
      )} />
    ),
    onClick: () => onChangeView('miniamaker-dashboard'),
  };

  const techBugTrackerLink = {
    label: "Suivi Bugs",
    href: "#",
    icon: (
      <Bug className={cn(
        "h-5 w-5 flex-shrink-0",
        isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
      )} />
    ),
    onClick: () => onChangeView('tech-bug-tracker'),
  };

  const userTopicsLink = {
    label: "Mes sujets",
    href: "#",
    icon: (
      <Lightbulb className={cn(
        "h-5 w-5 flex-shrink-0",
        isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
      )} />
    ),
    onClick: () => onChangeView('user-topics'),
  };

  const pbmTopicsLink = {
    label: "Sujets Clients",
    href: "#",
    icon: (
      <Lightbulb className={cn(
        "h-5 w-5 flex-shrink-0",
        isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
      )} />
    ),
    onClick: () => onChangeView('pbm-topics'),
  };


  return (
    <div className="h-screen flex-shrink-0">

      <Sidebar open={open} setOpen={handleOpenChange}>
        <SidebarBody className="justify-between gap-10">
          <div className="flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
            {/* User profile at top */}
            <button
              type="button"
              onClick={() => onChangeView('profile')}
              className={cn(
                "relative flex items-center group/sidebar rounded-lg transition-all duration-200 mb-4 overflow-visible ring-0 outline-none focus:ring-0 focus:outline-none",
                open ? "justify-start gap-2 px-3 py-2" : "justify-center w-9 h-9",
                currentView === 'profile'
                  ? "bg-neutral-300 dark:bg-neutral-700/60 text-neutral-900 dark:text-neutral-100"
                  : "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-300/50 dark:hover:bg-neutral-700/30"
              )}
            >
              <div className="flex-shrink-0 relative">
                {userProfile?.avatar_url ? (
                  <img 
                    src={userProfile.avatar_url} 
                    alt={displayName}
                    className="h-7 w-7 flex-shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div 
                    className="h-7 w-7 flex-shrink-0 rounded-full flex items-center justify-center text-white text-xs font-medium"
                    style={{ background: getGradientFromName(userProfile?.first_name, userProfile?.last_name) }}
                  >
                    {getInitials(userProfile?.first_name, userProfile?.last_name)}
                  </div>
                )}
                {hasProfileIncomplete && (
                  <div className="absolute top-0 right-0 w-2 h-2 bg-warning rounded-full ring-1 ring-background" />
                )}
              </div>

              {open && (
                <motion.div
                  initial={false}
                  animate={{ opacity: 1 }}
                  className="flex flex-col min-w-0 items-start group-hover/sidebar:translate-x-1 transition duration-150"
                >
                  <span className="text-sm whitespace-pre truncate">
                    {displayName}
                  </span>
                  {roleDisplayName && (
                    <span className="text-[11px] text-muted-foreground truncate leading-tight text-left">
                      {roleDisplayName}
                    </span>
                  )}
                </motion.div>
              )}
            </button>

            <div className="flex flex-col gap-2">
              {links.map((link) => (
                <SidebarLink
                  key={link.label}
                  link={link}
                  active={
                    (link.label === "Planning" && currentView === 'calendar') ||
                    (link.label === "Statistiques" && currentView === 'user-stats') ||
                    (link.label === "Assets" && currentView === 'assets') ||
                    (link.label === "Conversations" && currentView === 'staff-chat') ||
                    (link.label === "Messages" && currentView === 'customer-chat')
                  }
                />
              ))}
              
              {hasAccess('director_dashboard') && isAdmin && (
                <SidebarLink
                  link={directorShootingsLink}
                  active={currentView === 'director-dashboard'}
                />
              )}

              {hasAccess('admin_task_builder') && (
                <SidebarLink
                  link={opsTicketsLink}
                  active={currentView === 'ops-tickets'}
                />
              )}

              {hasAccess('csm_dashboard') && (
                <SidebarLink
                  link={csmDashboardLink}
                  active={currentView === 'csm-dashboard'}
                />
              )}

              {hasAccess('csm_dashboard') && (
                <SidebarLink
                  link={csmTasksLink}
                  active={currentView === 'csm-tasks'}
                />
              )}


              {hasAccess('miniatures') && (
                <SidebarLink
                  link={miniamakerDashboardLink}
                  active={currentView === 'miniamaker-dashboard'}
                />
              )}

              {hasAccess('tech_bug_tracker') && (
                <SidebarLink
                  link={techBugTrackerLink}
                  active={currentView === 'tech-bug-tracker'}
                />
              )}
              
              {hasAccess('user_topics') && (
                <SidebarLink
                  link={userTopicsLink}
                  active={currentView === 'user-topics'}
                />
              )}

              {hasAccess('pbm_topics') && (
                <SidebarLink
                  link={pbmTopicsLink}
                  active={currentView === 'pbm-topics'}
                />
              )}

              {hasAccess('admin_feature_access') && (
                <SidebarLink
                  link={{
                    label: "Product Board",
                    href: "#",
                    icon: (
                      <Rocket className={cn(
                        "h-5 w-5 flex-shrink-0",
                        isImpersonating ? "text-orange-300" : "text-neutral-700 dark:text-neutral-200"
                      )} />
                    ),
                    onClick: () => onChangeView('product-board'),
                  }}
                  active={currentView === 'product-board'}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {/* Impersonation indicator - clickable to open user profile */}
            {isImpersonating && impersonatedUser && (
              <button
                type="button"
                className={cn(
                  "rounded-lg border transition-all cursor-pointer text-left",
                  "bg-orange-500/20 border-orange-500/40 hover:bg-orange-500/30",
                  open ? "p-3" : "p-2"
                )}
                onClick={() => onChangeView('profile')}
                title="Voir le profil de cet utilisateur"
              >
                <div className={cn(
                  "flex items-center gap-2",
                  !open && "justify-center"
                )}>
                  <Avatar className="h-7 w-7 flex-shrink-0 ring-2 ring-orange-500">
                    <AvatarImage src={impersonatedUser.avatar_url || undefined} />
                    <AvatarFallback className="bg-orange-500 text-white text-xs">
                      {getImpersonatedInitials()}
                    </AvatarFallback>
                  </Avatar>
                  
                  {open && (
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-orange-200 font-medium truncate">
                        Vue de
                      </p>
                      <p className="text-sm text-orange-100 font-semibold truncate">
                        {getImpersonatedDisplayName(impersonatedUser)}
                      </p>
                    </div>
                  )}
                  
                  {open && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        stopImpersonation();
                      }}
                      className="p-1 rounded-md hover:bg-orange-500/30 text-orange-200 hover:text-white transition-colors"
                      title="Quitter la vue"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                {!open && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      stopImpersonation();
                    }}
                    className="mt-1 p-1 w-full flex justify-center rounded-md hover:bg-orange-500/30 text-orange-200 hover:text-white transition-colors"
                    title="Quitter la vue"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </button>
            )}
            
            {hasAccess('booking_access') && !hideBookingTab && (
              <SidebarLink
                link={bookingLink}
                active={currentView === 'scheduling'}
              />
            )}

            {isUserStaff && (
              <SidebarLink
                link={{
                  label: "Fixer un Bug",
                  href: "#",
                  icon: <Bug className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
                  onClick: (e?: React.MouseEvent) => {
                    e?.stopPropagation();
                    e?.preventDefault();
                    useBugReportStore.getState().captureScreenshot().then(() => {
                      setShowBugReportModal(true);
                    });
                  },
                }}
                active={false}
              />
            )}

            {isUserStaff && (
              <SidebarLink
                link={{
                  label: "Nouvelle Feature",
                  href: "#",
                  icon: <Sparkles className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />,
                  onClick: (e?: React.MouseEvent) => {
                    e?.stopPropagation();
                    e?.preventDefault();
                    setShowFeatureRequestModal(true);
                  },
                }}
                active={false}
              />
            )}
            
            <SidebarLink
              link={helpLink}
              active={currentView === 'help'}
            />
            
            {/* Admin Menu - show if user has access to at least one admin feature */}
            {adminMenuItems.some(item => {
              const service = VIEW_TO_SERVICE[item.view];
              return service ? hasAccess(service) : false;
            }) && (
              <AdminMenu 
                adminMenuOpen={adminMenuOpen} 
                setAdminMenuOpen={setAdminMenuOpen} 
                onChangeView={onChangeView}
                currentView={currentView}
                hasAccess={hasAccess}
                sidebarOpen={open}
                animate={true}
              />
            )}
          </div>
        </SidebarBody>
      </Sidebar>
      <BugReportModal
        open={showBugReportModal}
        onOpenChange={setShowBugReportModal}
        currentView={currentView}
      />
      <FeatureRequestModal
        open={showFeatureRequestModal}
        onOpenChange={setShowFeatureRequestModal}
        currentView={currentView}
      />
    </div>
  );
}

const Logo = () => {
  return (
    <a
      href="#"
      className="font-normal flex items-center text-sm py-1 relative z-20"
    >
      <motion.img
        layout
        layoutId="logo"
        src={prsnlLogo}
        alt="PRSNL"
        className="h-5 w-auto flex-shrink-0 invert"
      />
    </a>
  );
};

const LogoIcon = () => {
  return (
    <a
      href="#"
      className="font-normal flex items-center text-sm py-1 relative z-20"
    >
      <motion.img
        layout
        layoutId="logo"
        src={prsnlLogo}
        alt="PRSNL"
        className="h-5 w-auto flex-shrink-0 invert"
      />
    </a>
  );
};
